/**
 * Runtime configuration loader.
 *
 * The app fetches /config.json from the server before rendering.
 * This allows the same build artifact to be deployed across environments
 * (local, staging, production) by changing only config.json.
 *
 * Config source by environment:
 *   local      → public/config.local.json copied to public/config.json (via pnpm predev)
 *   staging    → public/config.json generated in CI from GitHub Environment vars
 *   production → public/config.json generated in CI from GitHub Environment vars
 */

export interface RuntimeConfig {
	/** tRPC base URL for the document API */
	DOCUMENT_API_BASE_URL: string;
	/** Base URL for authentication API */
	AUTH_API_BASE_URL: string;
	/** Pre-signed upload URL endpoint */
	UPLOAD_FILE_URL: string;
	/** ERP setup document upload URL endpoint */
	ERP_SETUP_DOC_URL: string;
	/** REST base URL for WebSocket Mgmt API */
	WEB_SOCKET_API: string;
	/** WSS endpoint for PubSub connections */
	PUBSUB_WEBSOCKET_ENDPOINT: string;
	/** URL for Electron app download */
	ELECTRON_DOWNLOAD_URL: string;
	/** URL for PDF.js worker */
	PDF_WORKER_URL: string;
}

/** Fallback config used when /config.json cannot be fetched (e.g. unit tests). */
const DEFAULT_CONFIG: RuntimeConfig = {
	DOCUMENT_API_BASE_URL: "http://localhost:3000/trpc",
	AUTH_API_BASE_URL: "http://localhost:3001",
	UPLOAD_FILE_URL: "http://localhost:3000/uploadUrl",
	ERP_SETUP_DOC_URL: "http://localhost:3000/setupDocUploadUrl",
	WEB_SOCKET_API: "http://localhost:3002",
	PUBSUB_WEBSOCKET_ENDPOINT: "ws://localhost:3002",
	ELECTRON_DOWNLOAD_URL: "http://localhost:5173/cdn/Fint.exe",
	PDF_WORKER_URL: "http://localhost:5173/cdn/pdf.worker.min.js",
};

let _config: RuntimeConfig = DEFAULT_CONFIG;
let _loaded = false;

/**
 * Fetches /config.json and stores the result.
 * Must be called and awaited before the React app is rendered.
 *
 * - Uses `cache: "no-store"` so that browsers always get the latest config
 *   on a fresh page load (avoids stale configs after a deployment).
 * - Falls back to DEFAULT_CONFIG if the request fails so the app still
 *   boots in environments where config.json is absent (e.g. unit tests).
 * - config.json must contain only public values (no secrets).
 */
export async function loadRuntimeConfig(): Promise<void> {
	if (_loaded) return;

	try {
		const response = await fetch("/config.json", {
			cache: "no-store",
			headers: { Accept: "application/json" },
		});

		if (!response.ok) {
			console.warn(
				`[config] /config.json returned HTTP ${response.status}. Falling back to defaults.`,
			);
			_loaded = true;
			return;
		}

		const data: Partial<RuntimeConfig> = await response.json();
		_config = { ...DEFAULT_CONFIG, ...data };
		_loaded = true;

		if (import.meta.env.DEV) {
			console.info("[config] Runtime config loaded");
		}
	} catch (err) {
		console.warn(
			"[config] Failed to load /config.json. Falling back to defaults.",
			err,
		);
		_loaded = true;
	}
}

/**
 * Returns the loaded RuntimeConfig.
 * Safe to call synchronously after `loadRuntimeConfig()` has resolved.
 */
export function getConfig(): RuntimeConfig {
	return _config;
}
